import { Type } from '@angular/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { WidgetType } from '../../shared/widgets/utils/widget-type';
import { DirtyFormGuard } from '../guards/dirty-form.guard';
import { RouteAllowedGuard } from '../guards/route-allowed.guard';
import { KeeperType } from '../resources/keepers.resource';

export const ROOT_PATH = 'navigation';
export const CUSTOM_PAGE_PATH = 'custom';
export const LOGIN_PATH = 'login';

export enum RootPath {
  MONITORING = 'monitoring',
  MAPS = 'maps',
  REPORTS = 'reports',
  EXPORT_FILES = 'export-files',
  CONFIGURATION = 'configuration',
  SYSCONFIG = 'sysconfig',
  ACCOUNT = 'account',
}

export enum MonitoringPath {
  DASHBOARD = 'dashboard',
  BATTERY = 'battery',
  AREA_ATTENDANCE = 'area_attendance',
  EMPLOYEES = 'employees',
  ALERTS = 'alerts',
  KEEPERS = 'keepers',
  ANCHORS = 'anchors',
}

export enum MapsPath {
  MAP_2D = 'map-2d',
  MAPS_SELECT = 'maps-select',
  MAP_3D = 'map-3d',
}

export enum ReportsPath {
  SERIES_CHART = 'series-chart',
  KEEPER_SUMMARY = 'keeper-summary',
  ACTIVITY = 'activity',
  ACTIVITY_ON_SITE = 'activity-on-site',
  AREAS_LOG = 'areas-log',
  SITES_LOG = 'sites-log',
  BUILDINGS_LOG = 'buildings-log',
  SITES_LOG_SUMMARY = 'sites-log-summary',
  DANGER_ZONES = 'danger-zones',
  UNIDENTIFIED_ACCESS = 'unidentified-access',
  TAG_HISTORY = 'tag-history',
  // CONTRACTORS = 'contractors',
  MILEAGE = 'mileage',
  MOTIONLESS = 'motionless',
  MOTIONLESS_SUMMARY = 'motionless-summary',
  // SERVICE_WORK = 'service-work',
  AUDIT = 'audit',
  AUDIT_SUMMARY = 'audit-summary',
  EVENTS = 'events',
  ANCHOR_EVENTS = 'anchor-events',
  TAG_EVENTS = 'tag-events',
  TAGZONE_EVENTS = 'tagzone-events',
  EXTERNAL_EVENTS = 'external-events',
  AREA_EVENTS = 'area-events',
  TAG_COMMANDS = 'tag-commands',
  SPEED_EXCEED = 'speed-exceed',
  CAS_EVENTS = 'cas-events',
  CAS_EVENTS_SUMMARY = 'cas-events-summary',
  CAS_EVENTS_SUMMARY_SERIES = 'cas-events-summary-series',
}

export enum ExportFilesPath {
  FILES_LIST = 'export-files-list',
  EXPORT_HISTORY = 'export-history',
}

export enum ConfigurationPath {
  EMPLOYEES = 'employees',
  ITEMS = 'items',
  VEHICLES = 'vehicles',
  EQUIPMENT = 'equipment',
  AREAS = 'areas',
  BUILDINGS = 'buildings',
  MAPS = 'maps',
  TAGS = 'tags',
  TAGS_CONFIG = 'tags-config',
  ANCHORS = 'anchors',
  USERS = 'users',
  ROLES = 'roles',
  LABELS = 'labels',
  CUSTOM_NODES = 'custom-nodes',
}

export enum SysconfigPath {
  EVENTS_CONFIG = 'events-config',
  SIZ_TYPES = 'siz-types',
  GAS_SENSOR_SPECS = 'gas-sensor-specs',
  REACTIONS = 'reactions',
  REPORT_TEMPLATES = 'report-templates',
  UI_CONFIG = 'ui-config',
  AUDIT = 'sys-audit',
}

export enum AccountPath {
  PASSWORD = 'password',
}

export type PathMap = {
  [key: string]: unknown;
  [RootPath.MONITORING]: MonitoringPath;
  [RootPath.MAPS]: MapsPath;
  [RootPath.REPORTS]: ReportsPath;
  [RootPath.EXPORT_FILES]: ExportFilesPath;
  [RootPath.CONFIGURATION]: ConfigurationPath;
  [RootPath.SYSCONFIG]: SysconfigPath;
  [RootPath.ACCOUNT]: AccountPath;
};

// Used to construct routes in angular routing modules
export interface ContainsRoute<T> {
  path: T;
}

export interface MainRouteDescription<T> extends ContainsRoute<T> {
  canLoad: [typeof RouteAllowedGuard],
  canActivate: [typeof RouteAllowedGuard],
  canActivateChild: [typeof RouteAllowedGuard],
}

export interface ContextRouteDescription<T> extends ContainsRoute<T> {
  component: Type<any>;
  canDeactivate: [typeof DirtyFormGuard];
}

// Custom page section
export type ReportCustomPage = {
  preset: number;
  report: ReportsPath;
};

export type CustomPageMap = {
  [RootPath.REPORTS]: ReportCustomPage;
};

// Personalization section
export type DashboardPersonalization = {
  updateTimeout: number;
  widgets: WidgetType[];
  widgetConfigs: {
    // [WidgetType.MOTIONLESS]: WidgetMotionlessConfig,
  };
};

export interface MapGroup {
  name: string;
  labels: string[];
}

export type MapsSelectPersonalization = {
  areasLayerTimeout: number;
  infrastructureLayerTimeout: number;
  tagsLayerTimeout: number;
  settingsPanelRefreshTimeout: number;
  sensorsRefreshTimeout: number;
  lastSignalStatusTimeout: number;
  mapGroups: MapGroup[];
};

export type Map3dPersonalization = {
  areasLayerTimeout: number;
  infrastructureLayerTimeout: number;
  tagsLayerTimeout: number;
};

export type EquipmentPersonalization = {
  tags: number[];
  tagSearchRefresh: number;
  tagSearchRefreshTimeout: number;
};

export type PersonalizationMap = {
  [MonitoringPath.DASHBOARD]: DashboardPersonalization;
  [MapsPath.MAPS_SELECT]: MapsSelectPersonalization;
  [MapsPath.MAP_3D]: Map3dPersonalization;
  [ConfigurationPath.EQUIPMENT]: EquipmentPersonalization;
};

// Configuration section
export type ConfigurationMap = {
  // No page specific configuration for now
};

// Main menu section
export type SubmenuItem = {
  // For navigation menu
  title: string;
  hidden?: boolean;
  // For UI config
  keeperType?: KeeperType;
  cantBeDisabled?: boolean;
  personalisable?: boolean;
  configurable?: boolean;
};

export type MainMenuItem<T extends RootPath> = {
  // For navigation menu
  title: string;
  icon: string;
  iconTheme: string;
  iconClass?: string;
  pages: { [P in PathMap[T]]: SubmenuItem };
  divider?: boolean;
  openInNewTab?: boolean;
  // For UI config
  hasCustomPages?: boolean;
};

export type MainMenu = {
  [P in RootPath]: MainMenuItem<P>;
};

export const mainMenu: MainMenu = {
  [RootPath.MONITORING]: {
    title: marker('navigation.menu.monitoring.title'),
    icon: 'pie_chart',
    iconTheme: 'runtime-theme',
    pages: {
      [MonitoringPath.DASHBOARD]: {
        title: marker('navigation.menu.monitoring.dashboard'),
        personalisable: true,
      },
      [MonitoringPath.EMPLOYEES]: {
        title: marker('navigation.menu.monitoring.employees'),
      },
      [MonitoringPath.ALERTS]: {
        title: marker('navigation.menu.monitoring.alerts'),
      },
      [MonitoringPath.BATTERY]: {
        title: marker('navigation.menu.monitoring.battery'),
      },
      [MonitoringPath.AREA_ATTENDANCE]: {
        title: marker('navigation.menu.monitoring.area_attendance'),
      },
      [MonitoringPath.KEEPERS]: {
        title: marker('navigation.menu.monitoring.keepers'),
      },
      [MonitoringPath.ANCHORS]: {
        title: marker('navigation.menu.monitoring.anchors'),
      },
    },
  },
  [RootPath.MAPS]: {
    title: marker('navigation.menu.maps.title'),
    icon: 'map',
    iconTheme: 'runtime-theme',
    pages: {
      [MapsPath.MAP_2D]: {
        title: marker('navigation.menu.maps.map-2d'),
        hidden: true,
      },
      [MapsPath.MAPS_SELECT]: {
        title: marker('navigation.menu.maps.maps-select'),
        personalisable: true,
      },
      [MapsPath.MAP_3D]: {
        title: marker('navigation.menu.maps.map-3d'),
        personalisable: true,
      },
    },
  },
  [RootPath.REPORTS]: {
    title: marker('navigation.menu.reports.title'),
    icon: 'receipt',
    iconTheme: 'history-theme',
    hasCustomPages: true,
    openInNewTab: true,
    pages: {
      [ReportsPath.SERIES_CHART]: {
        title: marker('navigation.menu.reports.seriesChart'),
      },
      [ReportsPath.KEEPER_SUMMARY]: {
        title: marker('navigation.menu.reports.keeperSummary'),
      },
      [ReportsPath.ACTIVITY]: {
        title: marker('navigation.menu.reports.activity'),
      },
      [ReportsPath.ACTIVITY_ON_SITE]: {
        title: marker('navigation.menu.reports.activityOnSite'),
      },
      [ReportsPath.AREAS_LOG]: {
        title: marker('navigation.menu.reports.areasLog'),
      },
      [ReportsPath.SITES_LOG]: {
        title: marker('navigation.menu.reports.sitesLog'),
      },
      [ReportsPath.SITES_LOG_SUMMARY]: {
        title: marker('navigation.menu.reports.sitesLogSummary'),
      },
      [ReportsPath.BUILDINGS_LOG]: {
        title: marker('navigation.menu.reports.buildingsLog'),
      },
      [ReportsPath.DANGER_ZONES]: {
        title: marker('navigation.menu.reports.dangerZones'),
      },
      [ReportsPath.SPEED_EXCEED]: {
        title: marker('navigation.menu.reports.speedExceed'),
      },
      /*
      [ReportsPath.CONTRACTORS]: {
        title: marker('navigation.menu.reports.contractors'),
      },
      */
      [ReportsPath.EVENTS]: {
        title: marker('navigation.menu.reports.events'),
      },
      [ReportsPath.MILEAGE]: {
        title: marker('navigation.menu.reports.mileage'),
      },
      [ReportsPath.MOTIONLESS]: {
        title: marker('navigation.menu.reports.motionless'),
      },
      [ReportsPath.MOTIONLESS_SUMMARY]: {
        title: marker('navigation.menu.reports.motionlessSummary'),
      },
      [ReportsPath.CAS_EVENTS]: {
        title: marker('navigation.menu.reports.casEvents'),
      },
      [ReportsPath.CAS_EVENTS_SUMMARY]: {
        title: marker('navigation.menu.reports.casEventsSummary'),
      },
      [ReportsPath.CAS_EVENTS_SUMMARY_SERIES]: {
        title: marker('navigation.menu.reports.casEventsSummarySeries'),
      },
      [ReportsPath.UNIDENTIFIED_ACCESS]: {
        title: marker('navigation.menu.reports.unidentifiedAccess'),
      },
      /*
      [ReportsPath.SERVICE_WORK]: {
        title: marker('navigation.menu.reports.serviceWork'),
      },
      */
      [ReportsPath.TAG_HISTORY]: {
        title: marker('navigation.menu.reports.tagHistory'),
      },
      [ReportsPath.AUDIT]: {
        title: marker('navigation.menu.reports.audit'),
      },
      [ReportsPath.AUDIT_SUMMARY]: {
        title: marker('navigation.menu.reports.auditSummary'),
      },
      [ReportsPath.EXTERNAL_EVENTS]: {
        title: marker('navigation.menu.reports.externalEvents'),
      },
      [ReportsPath.AREA_EVENTS]: {
        title: marker('navigation.menu.reports.areaEvents'),
      },
      [ReportsPath.ANCHOR_EVENTS]: {
        title: marker('navigation.menu.reports.anchorEvents'),
      },
      [ReportsPath.TAGZONE_EVENTS]: {
        title: marker('navigation.menu.reports.tagZoneEvents'),
      },
      [ReportsPath.TAG_EVENTS]: {
        title: marker('navigation.menu.reports.tagEvents'),
      },
      [ReportsPath.TAG_COMMANDS]: {
        title: marker('navigation.menu.reports.tagCommands'),
      },
    },
  },
  [RootPath.EXPORT_FILES]: {
    title: marker('navigation.menu.exportFiles.title'),
    icon: 'vertical_align_bottom',
    iconTheme: 'history-theme',
    pages: {
      [ExportFilesPath.FILES_LIST]: {
        title: marker('navigation.menu.exportFiles.exportFiles'),
      },
      [ExportFilesPath.EXPORT_HISTORY]: {
        title: marker('navigation.menu.exportFiles.exportHistory'),
      },
    },
  },
  [RootPath.CONFIGURATION]: {
    title: marker('navigation.menu.configuration.title'),
    icon: 'settings',
    iconTheme: 'configuration-theme',
    pages: {
      [ConfigurationPath.EMPLOYEES]: {
        title: marker('navigation.menu.configuration.employees'),
        keeperType: KeeperType.EMPLOYEE,
      },
      [ConfigurationPath.ITEMS]: {
        title: marker('navigation.menu.configuration.items'),
        keeperType: KeeperType.ITEM,
      },
      [ConfigurationPath.VEHICLES]: {
        title: marker('navigation.menu.configuration.vehicles'),
        keeperType: KeeperType.VEHICLE,
      },
      [ConfigurationPath.EQUIPMENT]: {
        title: marker('navigation.menu.configuration.equipment'),
        personalisable: true,
      },
      [ConfigurationPath.AREAS]: {
        title: marker('navigation.menu.configuration.areas'),
      },
      [ConfigurationPath.MAPS]: {
        title: marker('navigation.menu.configuration.maps'),
      },
      [ConfigurationPath.BUILDINGS]: {
        title: marker('navigation.menu.configuration.buildings'),
      },
      [ConfigurationPath.TAGS]: {
        title: marker('navigation.menu.configuration.tags'),
      },
      [ConfigurationPath.TAGS_CONFIG]: {
        title: marker('navigation.menu.configuration.tags-config'),
      },
      [ConfigurationPath.ANCHORS]: {
        title: marker('navigation.menu.configuration.anchors'),
      },
      [ConfigurationPath.CUSTOM_NODES]: {
        title: marker('navigation.menu.configuration.custom-nodes'),
      },
      [ConfigurationPath.USERS]: {
        title: marker('navigation.menu.configuration.users'),
      },
      [ConfigurationPath.ROLES]: {
        title: marker('navigation.menu.configuration.roles'),
      },
      [ConfigurationPath.LABELS]: {
        title: marker('navigation.menu.configuration.labels'),
      },
    },
  },
  [RootPath.SYSCONFIG]: {
    title: marker('navigation.menu.sysconfig.title'),
    icon: 'build',
    iconTheme: 'configuration-theme',
    pages: {
      [SysconfigPath.EVENTS_CONFIG]: {
        title: marker('navigation.menu.sysconfig.events'),
      },
      [SysconfigPath.SIZ_TYPES]: {
        title: marker('navigation.menu.sysconfig.sizTypes'),
      },
      [SysconfigPath.GAS_SENSOR_SPECS]: {
        title: marker('navigation.menu.sysconfig.gasSensorSpecs'),
      },
      [SysconfigPath.REACTIONS]: {
        title: marker('navigation.menu.sysconfig.reactions'),
      },
      [SysconfigPath.REPORT_TEMPLATES]: {
        title: marker('navigation.menu.sysconfig.reportTemplates'),
      },
      [SysconfigPath.UI_CONFIG]: {
        title: marker('navigation.menu.sysconfig.uiConfig'),
        cantBeDisabled: true,
      },
      [SysconfigPath.AUDIT]: {
        title: marker('navigation.menu.sysconfig.audit'),
      },
    },
  },
  [RootPath.ACCOUNT]: {
    title: marker('navigation.menu.account.title'),
    icon: 'lock',
    iconTheme: 'configuration-theme',
    iconClass: 'account-icon-color',
    divider: true,
    pages: {
      [AccountPath.PASSWORD]: {
        title: marker('navigation.menu.account.account'),
        cantBeDisabled: true,
      },
    },
  },
};
